/**
 * ValidateSurveyQuestions formats the survey data for email
 * @param { type, questions } parameter
 */
export const ValidateSurveyQuestions = ({ type, questions }) =>
  ({
    home: getHomeSurvey,
    boat: getBoatSurvey,
    animal: getAnimalSurvey,
    travel: getTravelSurvey,
    person: getPersonSurvey,
    forest: getForestSurvey,
    vehicle: getVehicleSurvey,
    valuable: getValuableSurvey,
  }[type](questions));

/**
 * getValue is to get the value from selected insurance answer
 * @param {*} answer
 */
const getValue = (answer) => {
  return (answer || { value: '' }).value;
};

/**
 * getSurvey is to prepare the survey question and answer
 * @param {*} questions
 */
const getSurvey = (questions) => {
  return Object.keys(questions)
    .map((questionType) => questions[questionType])
    .map(({ question, answer, dataKey }) => ({
      answer,
      question,
      id: dataKey,
    }));
};

/**
 * getVehicleSurvey is to prepare survey questions for vehicle insurance type
 * @param {*} questions
 */
const getVehicleSurvey = (questions) => {
  const type = getValue(questions.vehicleType);
  const museumType = getValue(questions.museumVehicleType);
  const vehicles = ['Car', 'Van', 'Lorry'];

  if (type !== 'Museum vehicle') {
    delete questions.museumVehicleType;
    if (vehicles.indexOf(type) < 0) delete questions.vehicleValue;
  } else if (vehicles.indexOf(museumType) < 0) {
    delete questions.vehicleValue;
  }

  return getSurvey(questions);
};

/**
 * getHomeSurvey is to prepare survey questions for home insurance type
 * @param {*} questions
 */
const getHomeSurvey = (questions) => {
  const type = getValue(questions.homeType);
  const homesWithArea = [
    'Apartment building',
    'Terraced house',
    'Detached house',
    'Duplex',
  ];

  if (homesWithArea.indexOf(type) > -1) {
    const questionType = `totalfloorarea${type === 'Duplex' ? 'detached' : ''}`;
    delete questions[
      getValue(questions.housingcompany) === 'No' ? 'floorarea' : questionType
    ];
  } else {
    delete questions[
      getValue(questions.housingcompany) === 'Yes'
        ? 'totalfloorarea'
        : 'floorarea'
    ];
    if (getValue(questions.waterPipeSystem) !== 'Yes')
      delete questions.YearOfWaterPipeSystem;
    if (getValue(questions.heatingSystem) !== 'Hydronic radiant heat')
      delete questions.YearOfHeatingSystem;
  }

  return getSurvey(questions);
};

const getChildUnbonSurvey = (type, questions) => {
  if (
    type === 'Child' &&
    getValue(questions.competitiveSportsChild) !== 'Yes'
  ) {
    delete questions.whichSportsChild;
  } else if (
    type === 'Unborn child or twins' &&
    getValue(questions.motherChild) !== 'No'
  ) {
    delete questions.mothersID;
  }
  return getSurvey(questions);
};

/**
 * getPersonSurvey is to prepare survey questions for person insurance type
 * @param {*} questions
 */
const getPersonSurvey = (questions) => {
  const type = getValue(questions.personType);
  const person = ['Me', 'Another adult'];

  if (person.indexOf(type) > -1) {

    if (getValue(questions.health) === 'No') {
      delete questions.healthCoverage;
      delete questions.competitiveSportsMe;
      delete questions.competitiveSportsAnotherAdult;
      delete questions.whichSportsMee;
      delete questions.whichSportsAnotherAdult;
    } else {
      if (getValue(questions.competitiveSportsMe) !== 'Yes') {
        delete questions.whichSportsMee;
      }

      if (getValue(questions.competitiveSportsAnotherAdult) !== 'Yes') {
        delete questions.whichSportsAnotherAdult;
      }
    }
  } else {
    return getChildUnbonSurvey(type, questions);
  }
  return getSurvey(questions);
};

/**
 * getTravelSurvey is to prepare survey questions for travel insurance type
 * @param {*} questions
 */
const getTravelSurvey = (questions) => {
  const type = getValue(questions.travelType);

  if (type === 'Me' && getValue(questions.competitiveSportsMe) !== 'Yes') {
    delete questions.whichSportsMe;
  }

  if (type === 'Me' && getValue(questions.luggageInsuranceMe) !== 'Yes') {
    delete questions.insuranceIncludesMe;
  }

  if (
    type === 'Family member' &&
    getValue(questions.competitiveSportsFamilyMember) !== 'Yes'
  ) {
    delete questions.whichSportsFamilyMember;
  }

  return getSurvey(questions);
};

/**
 * getAnimalSurvey is to prepare survey questions for animal insurance type
 * @param {*} questions
 */
const getAnimalSurvey = (questions) => {
  return getSurvey(questions);
};

/**
 * getBoatSurvey is to prepare survey questions for boat insurance type
 * @param {*} questions
 */
const getBoatSurvey = (questions) => {
  const segmentLength = 3;
  const type = getValue(questions.boatType);
  const hasEngine = getValue(questions.boatEngine);
  const primaryengine = getValue(questions.primaryengines);
  const data = [...Array(segmentLength).keys()].reverse();
  data.splice(segmentLength-primaryengine);

  if (type === 'Sailboat' && hasEngine !== 'Yes') {
    delete questions.primaryengines;
    Array(segmentLength)
      ?.fill()
      ?.map((item, index) => {
        delete questions[`engineBrand-${index+1}`];
        delete questions[`engineProductionNumber-${index+1}`];
        delete questions[`engineYearOfManufacture-${index+1}`];
        delete questions[`enginePower-${index+1}`];
        return item;
      })    
  } else {
    data.forEach((index) => {
      delete questions[`engineBrand-${index+1}`];
      delete questions[`engineProductionNumber-${index+1}`];
      delete questions[`engineYearOfManufacture-${index+1}`];
      delete questions[`enginePower-${index+1}`];
    })
  }

  return getSurvey(questions);
};

/**
 * getForestSurvey is to prepare survey questions for forest insurance type
 * @param {*} questions
 */
const getForestSurvey = (questions) => {
  return getSurvey(questions);
};

/**
 * getValuableSurvey is to prepare survey questions for valuable insurance type
 * @param {*} questions
 */
const getValuableSurvey = (questions) => {
  return getSurvey(questions);
};
